import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TargetIcon, Share2Icon, CameraIcon, CheckCircle2Icon, ArrowRightIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
export function HomePage() {
  return <div className="w-full" data-id="element-173">
      {/* HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center pt-24 pb-20 px-6 md:px-12 overflow-hidden" data-id="element-174">
        {/* Decorative background elements */}
        <div className="absolute top-1/4 -right-20 w-96 h-96 bg-sage/30 rounded-full blur-3xl -z-10" data-id="element-175" />
        <div className="absolute bottom-1/4 -left-20 w-80 h-80 bg-coral/10 rounded-full blur-3xl -z-10" data-id="element-176" />

        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-177">
          <div className="lg:col-span-8 z-10" data-id="element-178">
            <motion.h1 initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="text-5xl md:text-7xl font-heading font-bold text-teal leading-[1.1] tracking-tight mb-8" data-id="element-179">
              Content that connects.
              <br data-id="element-180" />
              <span className="text-coral" data-id="element-181">Strategy that converts.</span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2,
            ease: 'easeOut'
          }} className="text-lg md:text-xl text-teal/80 max-w-2xl mb-10 leading-relaxed" data-id="element-182">
              Fore River Creative Co. helps brands grow through intentional
              marketing, elevated content, and social strategies designed to
              drive real results.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4,
            ease: 'easeOut'
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-183">
              <Link to="/portfolio" data-id="element-184">
                <motion.button whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.98
              }} className="w-full sm:w-auto bg-coral text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-coral-light transition-colors shadow-lg shadow-coral/20" data-id="element-185">
                  View Our Work
                </motion.button>
              </Link>
              <Link to="/contact" data-id="element-186">
                <motion.button whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.98
              }} className="w-full sm:w-auto bg-transparent border-2 border-teal text-teal px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal hover:text-cream transition-colors" data-id="element-187">
                  Start a Project
                </motion.button>
              </Link>
            </motion.div>
          </div>

          <div className="hidden lg:block lg:col-span-4 relative" data-id="element-188">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 0.3
          }} className="relative w-full aspect-[4/5] rounded-3xl overflow-hidden bg-sage/40 border border-sage/60 p-4" data-id="element-189">
              <div className="w-full h-full rounded-2xl bg-teal/5 flex items-center justify-center overflow-hidden relative" data-id="element-190">
                {/* Abstract composition representing content/strategy */}
                <div className="absolute top-10 left-10 w-32 h-32 bg-coral rounded-full mix-blend-multiply opacity-80 animate-pulse" style={{
                animationDuration: '4s'
              }} data-id="element-191" />
                <div className="absolute bottom-20 right-10 w-40 h-40 bg-teal rounded-full mix-blend-multiply opacity-20" data-id="element-192" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-64 border-2 border-teal/30 rounded-xl backdrop-blur-sm" data-id="element-193" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUE PROPOSITION SECTION */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white/40" data-id="element-194">
        <div className="max-w-7xl mx-auto" data-id="element-195">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center" data-id="element-196">
            <ScrollReveal data-id="element-197">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-teal leading-tight" data-id="element-198">
                More than content.
                <br data-id="element-199" />A strategy behind every post.
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-200">
              <p className="text-lg md:text-xl text-teal/80 leading-relaxed" data-id="element-201">
                We don't just create visuals—we build brand presence. Every
                piece of content is designed with purpose, backed by strategy,
                and aligned with your business goals.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW SECTION */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-id="element-202">
        <div className="max-w-7xl mx-auto" data-id="element-203">
          <ScrollReveal className="text-center mb-16" data-id="element-204">
            <h2 className="text-4xl font-heading font-bold text-teal mb-6" data-id="element-205">
              Our Expertise
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-206">
            {[{
            icon: <TargetIcon size={32} className="text-coral" data-id="element-207" />,
            title: 'Marketing Strategy',
            desc: 'Clear direction, defined audience targeting, and actionable plans that move your brand forward.'
          }, {
            icon: <Share2Icon size={32} className="text-coral" data-id="element-208" />,
            title: 'Social Media Management',
            desc: 'Consistent, on-brand content and management that keeps your business visible and relevant.'
          }, {
            icon: <CameraIcon size={32} className="text-coral" data-id="element-209" />,
            title: 'Content Creation',
            desc: 'High-quality, scroll-stopping visuals designed to engage your audience and convert.'
          }].map((service, idx) => <ScrollReveal key={idx} delay={idx * 0.1} data-id="element-210">
                <motion.div whileHover={{
              y: -10
            }} className="bg-white/60 backdrop-blur-sm p-10 rounded-3xl h-full border border-white/50 shadow-sm hover:shadow-md transition-all" data-id="element-211">
                  <div className="w-16 h-16 bg-cream rounded-2xl flex items-center justify-center mb-8" data-id="element-212">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-teal mb-4" data-id="element-213">
                    {service.title}
                  </h3>
                  <p className="text-teal/70 leading-relaxed" data-id="element-214">{service.desc}</p>
                </motion.div>
              </ScrollReveal>)}
          </div>

          <ScrollReveal className="text-center" data-id="element-215">
            <Link to="/services" className="inline-flex items-center gap-2 text-coral font-bold text-lg hover:text-coral-light transition-colors group" data-id="element-216">
              Explore Services
              <motion.span group-hover={{
              x: 5
            }} transition={{
              duration: 0.2
            }} data-id="element-217">
                <ArrowRightIcon size={20} data-id="element-218" />
              </motion.span>
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* RESULTS / DIFFERENTIATION SECTION */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-sage/20" data-id="element-219">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-220">
          <ScrollReveal direction="right" data-id="element-221">
            <div className="aspect-square rounded-3xl overflow-hidden relative bg-teal/5 border border-teal/10" data-id="element-222">
              <div className="absolute inset-0 flex items-center justify-center p-12" data-id="element-223">
                <div className="w-full h-full border-2 border-dashed border-teal/20 rounded-full animate-[spin_60s_linear_infinite] relative" data-id="element-224">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-coral rounded-full" data-id="element-225" />
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-12 h-12 bg-teal rounded-full" data-id="element-226" />
                  <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-sage rounded-full" data-id="element-227" />
                </div>
                <div className="absolute text-center" data-id="element-228">
                  <span className="block text-6xl font-heading font-bold text-teal mb-2" data-id="element-229">
                    100%
                  </span>
                  <span className="text-teal/60 font-medium uppercase tracking-widest text-sm" data-id="element-230">
                    Intentional
                  </span>
                </div>
              </div>
            </div>
          </ScrollReveal>

          <div data-id="element-231">
            <ScrollReveal data-id="element-232">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-teal mb-6" data-id="element-233">
                Built for growth.
              </h2>
              <p className="text-lg text-teal/80 mb-10 leading-relaxed" data-id="element-234">
                Our approach is rooted in strategy and execution. We focus on
                what actually works—creating content that not only looks good
                but performs.
              </p>
            </ScrollReveal>

            <div className="space-y-6" data-id="element-235">
              {['Strategy-first approach', 'Consistent brand voice', 'Data-aware content decisions', 'Designed to convert, not just exist'].map((point, idx) => <ScrollReveal key={idx} delay={0.2 + idx * 0.1} data-id="element-236">
                  <div className="flex items-start gap-4" data-id="element-237">
                    <CheckCircle2Icon className="text-coral shrink-0 mt-1" size={24} data-id="element-238" />
                    <span className="text-xl text-teal font-medium" data-id="element-239">
                      {point}
                    </span>
                  </div>
                </ScrollReveal>)}
            </div>
          </div>
        </div>
      </section>

      {/* PORTFOLIO PREVIEW SECTION */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-id="element-240">
        <div className="max-w-7xl mx-auto" data-id="element-241">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16" data-id="element-242">
            <ScrollReveal className="max-w-2xl" data-id="element-243">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-teal mb-6" data-id="element-244">
                Recent Work
              </h2>
              <p className="text-lg text-teal/80" data-id="element-245">
                A look at how we bring brands to life through content and
                strategy.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-246">
              <Link to="/portfolio" className="hidden md:inline-flex items-center gap-2 text-coral font-bold text-lg hover:text-coral-light transition-colors" data-id="element-247">
                View Full Portfolio <ArrowRightIcon size={20} data-id="element-248" />
              </Link>
            </ScrollReveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-249">
            {[{
            bg: 'bg-sage',
            label: 'Brand Identity',
            title: 'Local Coffee Roasters'
          }, {
            bg: 'bg-teal/20',
            label: 'Social Media',
            title: 'Boutique Fitness Studio'
          }, {
            bg: 'bg-coral/10',
            label: 'Content Creation',
            title: 'Skincare Launch'
          }].map((item, idx) => <ScrollReveal key={idx} delay={idx * 0.1} data-id="element-250">
                <Link to="/portfolio" data-id="element-251">
                  <motion.div whileHover="hover" className={`aspect-[4/5] rounded-2xl ${item.bg} relative overflow-hidden group cursor-pointer`} data-id="element-252">
                    <motion.div variants={{
                  hover: {
                    opacity: 1
                  }
                }} initial={{
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} className="absolute inset-0 bg-teal/80 flex flex-col justify-end p-8" data-id="element-253">
                      <span className="text-sage font-medium mb-2" data-id="element-254">
                        {item.label}
                      </span>
                      <h3 className="text-2xl font-heading font-bold text-white" data-id="element-255">
                        {item.title}
                      </h3>
                    </motion.div>
                  </motion.div>
                </Link>
              </ScrollReveal>)}
          </div>

          <div className="md:hidden text-center" data-id="element-256">
            <Link to="/portfolio" className="inline-flex items-center gap-2 text-coral font-bold text-lg hover:text-coral-light transition-colors" data-id="element-257">
              View Full Portfolio <ArrowRightIcon size={20} data-id="element-258" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-32 px-6 md:px-12 bg-teal text-center" data-id="element-259">
        <div className="max-w-3xl mx-auto" data-id="element-260">
          <ScrollReveal data-id="element-261">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-cream mb-6" data-id="element-262">
              Ready to elevate your brand?
            </h2>
            <p className="text-xl text-cream/80 mb-12" data-id="element-263">
              Let's create something that actually moves your business forward.
            </p>
            <Link to="/contact" data-id="element-264">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="bg-coral text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-coral-light transition-colors shadow-xl shadow-coral/20" data-id="element-265">
                Get in Touch
              </motion.button>
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}